import { ChevronDown } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { LmsInput } from '@shared/ui'
import '../profile-layout.css'

function normalizeSearchText(value: string) {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
}

export function ProfileComboboxInput({
  id,
  onChange,
  options,
  placeholder,
  value,
}: {
  readonly id: string
  readonly onChange: (nextValue: string) => void
  readonly options: readonly string[]
  readonly placeholder?: string
  readonly value: string
}) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [hasTyped, setHasTyped] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const [listStyle, setListStyle] = useState<{ left: number; top: number; width: number } | null>(null)

  const visibleOptions = useMemo(() => {
    if (!hasTyped) {
      return options
    }

    const query = normalizeSearchText(value)
    if (!query) {
      return options
    }

    return options.filter((option) => normalizeSearchText(option).includes(query))
  }, [hasTyped, options, value])

  const closeList = useCallback(() => {
    setIsOpen(false)
    setHasTyped(false)
    setHighlightIndex(-1)
  }, [])

  const openList = ({ preserveTyped = false }: { readonly preserveTyped?: boolean } = {}) => {
    const rect = rootRef.current?.getBoundingClientRect()
    if (rect) {
      const listMaxHeight = 256
      const spaceBelow = window.innerHeight - rect.bottom
      const openUpward = spaceBelow < listMaxHeight + 16 && rect.top > spaceBelow
      setListStyle({
        left: rect.left,
        top: openUpward ? Math.max(rect.top - listMaxHeight - 4, 8) : rect.bottom + 4,
        width: rect.width,
      })
    }

    if (!preserveTyped) {
      setHasTyped(false)
    }
    setHighlightIndex(-1)
    setIsOpen(true)
  }

  // Close when the page scrolls or resizes so the portalled list never detaches
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handleScroll = (event: Event) => {
      if (listRef.current && event.target instanceof Node && listRef.current.contains(event.target)) {
        return
      }

      closeList()
    }
    const handleResize = () => closeList()

    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [closeList, isOpen])

  const selectOption = (option: string) => {
    onChange(option)
    closeList()
  }

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null
    if (
      rootRef.current?.contains(nextTarget) ||
      listRef.current?.contains(nextTarget)
    ) {
      return
    }

    closeList()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!isOpen) {
        openList()
        return
      }

      const delta = event.key === 'ArrowDown' ? 1 : -1
      setHighlightIndex((previous) => {
        const next = previous + delta
        if (next < 0) return visibleOptions.length - 1
        if (next >= visibleOptions.length) return 0
        return next
      })
      return
    }

    if (event.key === 'Enter' && isOpen && highlightIndex >= 0) {
      event.preventDefault()
      const option = visibleOptions[highlightIndex]
      if (option !== undefined) {
        selectOption(option)
      }
      return
    }

    if (event.key === 'Escape' && isOpen) {
      event.preventDefault()
      closeList()
    }
  }

  return (
    <div className="profileCombobox" onBlur={handleBlur} ref={rootRef}>
      <LmsInput
        aria-activedescendant={
          isOpen && highlightIndex >= 0 ? `${id}-option-${highlightIndex}` : undefined
        }
        aria-autocomplete="list"
        aria-controls={`${id}-listbox`}
        aria-expanded={isOpen}
        autoComplete="off"
        id={id}
        onChange={(event) => {
          onChange(event.target.value)
          setHasTyped(true)
          setHighlightIndex(-1)
          if (!isOpen) openList({ preserveTyped: true })
        }}
        onFocus={() => openList()}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        role="combobox"
        value={value}
      />
      <button
        aria-label={isOpen ? 'Close options' : 'Open options'}
        className="profileComboboxToggle"
        onClick={() => {
          if (isOpen) {
            closeList()
          } else {
            openList()
          }
        }}
        tabIndex={-1}
        type="button"
      >
        <ChevronDown aria-hidden="true" size={16} />
      </button>

      {isOpen && visibleOptions.length > 0 && listStyle
        ? createPortal(
            <ul
              className="profileComboboxList"
              id={`${id}-listbox`}
              ref={listRef}
              role="listbox"
              style={{
                left: listStyle.left,
                maxHeight: 256,
                position: 'fixed',
                top: listStyle.top,
                width: listStyle.width,
              }}
            >
              {visibleOptions.map((option, index) => (
                <li
                  aria-selected={option === value}
                  className={`profileComboboxOption${index === highlightIndex ? ' is-highlighted' : ''}`}
                  id={`${id}-option-${index}`}
                  key={option}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    selectOption(option)
                  }}
                  onMouseEnter={() => setHighlightIndex(index)}
                  role="option"
                >
                  {option}
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </div>
  )
}
